/**
 * Agent photos — one place that answers "what picture do we show for this
 * Digital Workforce agent?", so every surface (chat bar, New Chat, Digital
 * Team strip, Command Center, a command's conversation, Digital Workforce)
 * draws the same face and updates the moment it changes.
 *
 *   1. A logo uploaded on the agent's profile, if any.
 *   2. Otherwise the built-in photo for Momentum's default agents.
 *   3. Otherwise nothing — callers fall back to colour + initials.
 *
 * Uploaded logos are stored server-side (MomentumAMPAPI /api/AgentLogo, image
 * in a public blob container), so they follow the agent across browsers and
 * devices. The signed-in user's logos are loaded once into memory, which keeps
 * lookups synchronous for render code. Only an agent's owner can change it.
 */
import { useEffect, useState } from 'react';
import { getDefaultAgentVisual } from '../features/digitalworkforce/agentVisuals';
import { listAgentLogos, uploadAgentLogo, deleteAgentLogo } from '../services/agentLogoService';

/** Where logos lived before they moved server-side — migrated once, then cleared. */
const LEGACY_STORAGE_KEY = 'ig-agent-photos';
const CHANGE_EVENT = 'ig-agent-photos-changed';
/** Rendered as large as ~56px; 2× for sharp retina rendering. */
const PHOTO_PX = 128;

let logos: Record<string, string> = {};
let loadedForToken: string | null = null;
let loading: Promise<void> | null = null;

function emitChange(): void {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function currentToken(): string {
  try { return localStorage.getItem('authToken') || ''; } catch { return ''; }
}

/**
 * Loads the signed-in user's logos (once per user; `force` reloads). Safe to
 * call from anywhere — concurrent calls share one request.
 */
export function ensureAgentLogosLoaded(force = false): Promise<void> {
  const token = currentToken();
  if (!token) return Promise.resolve();
  if (!force && loadedForToken === token) return loading ?? Promise.resolve();
  if (loadedForToken !== token) logos = {};
  loadedForToken = token;
  const p = listAgentLogos()
    .then(async (items) => {
      if (loadedForToken !== token) return;
      logos = Object.fromEntries(items.map((i) => [i.agentId, i.logoUrl]));
      emitChange();
      await migrateLegacyLogos();
    })
    .catch(() => {
      // Logos are cosmetic — leave the initials in place and retry on the next call.
      if (loadedForToken === token) loadedForToken = null;
    })
    .finally(() => { if (loading === p) loading = null; });
  loading = p;
  return p;
}

/**
 * One-time move of logos saved in this browser (the old localStorage store)
 * to the server. The API only accepts the agent's owner, so other people's
 * entries are simply dropped.
 */
async function migrateLegacyLogos(): Promise<void> {
  let legacy: Record<string, string> = {};
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return;
    legacy = JSON.parse(raw) || {};
  } catch { /* unreadable — just clear it */ }
  for (const [agentId, dataUrl] of Object.entries(legacy)) {
    if (logos[agentId] || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) continue;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      logos[agentId] = await uploadAgentLogo(agentId, blob);
      emitChange();
    } catch { /* not the owner, or the agent is gone */ }
  }
  try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { }
}

/** The logo uploaded for this agent, if any. */
export function getUploadedAgentPhoto(agentId: string | null | undefined): string | undefined {
  if (!agentId) return undefined;
  if (loadedForToken !== currentToken()) void ensureAgentLogosLoaded();
  return logos[agentId];
}

/**
 * The picture to show for an agent: uploaded logo, else the built-in photo
 * for a default agent (matched by display name), else undefined.
 */
export function agentPhotoFor(agentId: string | null | undefined, displayName?: string): string | undefined {
  return getUploadedAgentPhoto(agentId) ?? (displayName ? getDefaultAgentVisual(displayName)?.image : undefined);
}

/**
 * Same as agentPhotoFor, but for small avatar tiles: a default agent gets its
 * transparent head-and-shoulders cutout, so it sits on the neutral tile
 * instead of a busy office scene. Uploaded logos are used as-is.
 */
export function agentAvatarFor(agentId: string | null | undefined, displayName?: string): string | undefined {
  return getUploadedAgentPhoto(agentId) ?? (displayName ? getDefaultAgentVisual(displayName)?.avatar : undefined);
}

/** Crops the image to a centred square and scales it to PHOTO_PX, as PNG. */
async function toSquarePng(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file (PNG, JPG, SVG or WebP).');
  if (file.size > 8 * 1024 * 1024) throw new Error('That image is over 8 MB — choose a smaller one.');
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("That image couldn't be read — try a PNG or JPG."));
      el.src = url;
    });
    const side = Math.min(img.naturalWidth || PHOTO_PX, img.naturalHeight || PHOTO_PX);
    const sx = ((img.naturalWidth || side) - side) / 2;
    const sy = ((img.naturalHeight || side) - side) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = PHOTO_PX;
    canvas.height = PHOTO_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("This browser can't process images.");
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, side, side, 0, 0, PHOTO_PX, PHOTO_PX);
    // PNG keeps transparent logos transparent; small at this size anyway.
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't process that image."))), 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Uploads a new logo for an agent you own. Throws with a user-facing message. */
export async function setAgentPhotoFromFile(agentId: string, file: File): Promise<void> {
  const png = await toSquarePng(file);
  logos = { ...logos, [agentId]: await uploadAgentLogo(agentId, png) };
  emitChange();
}

/** Removes an agent's logo. Throws with a user-facing message. */
export async function removeAgentPhoto(agentId: string): Promise<void> {
  await deleteAgentLogo(agentId);
  if (!(agentId in logos)) return;
  const next = { ...logos };
  delete next[agentId];
  logos = next;
  emitChange();
}

/**
 * Re-renders the caller whenever any agent's logo changes, so photo lookups
 * made during render pick up the new image. Also kicks off the initial load
 * and reloads when a different user signs in.
 */
export function useAgentPhotosVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    // A new token means a (possibly different) user — ensureAgentLogosLoaded
    // reloads on its own when the token changed, and shares one request.
    const onAuthChange = () => { void ensureAgentLogosLoaded(); };
    window.addEventListener(CHANGE_EVENT, bump);
    window.addEventListener('authTokenUpdated', onAuthChange);
    void ensureAgentLogosLoaded();
    return () => {
      window.removeEventListener(CHANGE_EVENT, bump);
      window.removeEventListener('authTokenUpdated', onAuthChange);
    };
  }, []);
  return version;
}
